import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, Clock, Activity, AlertTriangle, MessageCircle, Loader2, Bot, Calendar } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type Stat = { label: string; value: number | string };
type RecentItem = {
  id: number;
  kind: string;
  title: string;
  body: string;
  ts: string;
  severity?: string | null;
};
type DailyPoint = { date: string; count: number };
type Overview = {
  agent: { id: string; name: string; role: string; color: string; active: boolean };
  cron: { schedule: string; description: string; nextRun: string | null };
  synopsis: Stat[];
  daily?: DailyPoint[];
  recent: RecentItem[];
  note?: string;
};

const sevColor: Record<string, string> = {
  routine: "text-muted-foreground bg-muted border-border",
  monitoring: "text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-500/10 dark:border-blue-500/30",
  attention: "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30",
  critical: "text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-500/10 dark:border-red-500/30",
};

function adminHeaders(): HeadersInit {
  const token = localStorage.getItem("admin_token") || "";
  return { "x-admin-token": token };
}

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function fmtNextRun(iso: string | null): string {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "due";
  const m = Math.round(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h}h`;
  const d = Math.round(h / 24);
  return `in ${d}d`;
}

export default function AdminAgentDetail({ agent }: { agent: string }) {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/agents/${agent}/overview`, { headers: adminHeaders() });
      if (r.status === 401 || r.status === 403) { setError("Admin session expired. Please sign in again."); setLoading(false); return; }
      if (!r.ok) { setError(`Couldn't load (HTTP ${r.status}).`); setLoading(false); return; }
      setData(await r.json());
    } catch (e: any) {
      setError(e?.message || "Network error");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agent]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link href="/admin/agents">
          <span className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 cursor-pointer"><ArrowLeft className="w-3 h-3" /> Agents</span>
        </Link>
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 px-4 py-3 text-sm">
          {error || "No data"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid={`admin-agent-${data.agent.id}`}>
      <div>
        <Link href="/admin/agents">
          <span className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 cursor-pointer"><ArrowLeft className="w-3 h-3" /> Agents</span>
        </Link>
      </div>

      <header className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
          style={{ background: data.agent.color }}
        >
          {data.agent.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-serif font-bold tracking-tight">{data.agent.name}</h1>
            <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-md border ${data.agent.active ? "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/30" : "text-muted-foreground bg-muted border-border"}`}>
              {data.agent.active ? "active" : "standby"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{data.agent.role}</p>
        </div>
      </header>

      {/* Cron */}
      <section className="rounded-xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Schedule</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Cron</p>
            <p className="text-foreground">{data.cron.schedule}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Next run</p>
            <p className="text-foreground">{fmtNextRun(data.cron.nextRun)}{data.cron.nextRun ? ` · ${new Date(data.cron.nextRun).toLocaleString("en-IN")}` : ""}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Job</p>
            <p className="text-muted-foreground text-xs">{data.cron.description}</p>
          </div>
        </div>
      </section>

      {/* Synopsis */}
      <section className="rounded-xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Synopsis</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {data.synopsis.map((s, i) => (
            <div key={i} className="rounded-lg border bg-muted/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
              <p className="text-2xl font-bold text-foreground mt-1">{s.value}</p>
            </div>
          ))}
        </div>
        {data.note && (
          <p className="mt-4 text-xs text-amber-700 dark:text-amber-300/80 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {data.note}
          </p>
        )}
      </section>

      {/* Calendar */}
      {data.daily && data.daily.length > 0 && (
        <ActivityCalendar daily={data.daily} color={data.agent.color} recent={data.recent} />
      )}

      {/* Recent activity */}
      <section className="rounded-xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Recent activity</h2>
        </div>
        {data.recent.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Bot className="w-6 h-6 opacity-50" />
            <span>No activity yet.</span>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {data.recent.map(r => (
              <li key={r.id} className="py-3" data-testid={`recent-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                    {r.body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{r.body}</p>}
                  </div>
                  <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                    <span className="text-[10px] text-muted-foreground font-mono">{fmtRelative(r.ts)}</span>
                    {r.severity && (
                      <span className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded border ${sevColor[r.severity] || sevColor.routine}`}>
                        {r.severity}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ActivityCalendar({ daily, color, recent }: { daily: DailyPoint[]; color: string; recent: RecentItem[] }) {
  const [selected, setSelected] = useState<string | null>(null);
  const max = Math.max(1, ...daily.map(d => d.count));
  const total = daily.reduce((a, b) => a + b.count, 0);
  const activeDays = daily.filter(d => d.count > 0).length;

  function cellStyle(count: number): { background: string; color: string; borderColor: string } {
    if (count === 0) {
      return { background: "transparent", color: "var(--muted-foreground, #888)", borderColor: "var(--border, #e5e7eb)" };
    }
    const ratio = Math.min(1, count / max);
    const opacity = 0.25 + ratio * 0.75;
    return { background: hexWithAlpha(color, opacity), color: ratio > 0.5 ? "#fff" : color, borderColor: hexWithAlpha(color, Math.min(1, opacity + 0.1)) };
  }

  function hexWithAlpha(hex: string, alpha: number): string {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  function fmtDay(iso: string): string {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  }
  function fmtFull(iso: string): string {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
  }

  const itemsForSelected = selected
    ? recent.filter(r => r.ts.slice(0, 10) === selected)
    : [];

  return (
    <section className="rounded-xl border bg-card p-5" data-testid="agent-calendar">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Last 30 days</h2>
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          {total} actions · {activeDays}/30 days active
        </div>
      </div>

      <div className="grid grid-cols-10 gap-1.5 mb-4">
        {daily.map(d => {
          const s = cellStyle(d.count);
          const isSelected = selected === d.date;
          return (
            <button
              key={d.date}
              onClick={() => setSelected(isSelected ? null : d.date)}
              title={`${fmtFull(d.date)} — ${d.count} action${d.count === 1 ? "" : "s"}`}
              className={`aspect-square rounded-md border flex flex-col items-center justify-center text-[10px] font-mono transition-all ${
                isSelected ? "ring-2 ring-foreground/60 scale-110" : "hover:ring-1 hover:ring-foreground/20"
              }`}
              style={{ background: s.background, color: s.color, borderColor: s.borderColor }}
              data-testid={`day-${d.date}`}
            >
              <span className="leading-none">{new Date(d.date + "T00:00:00Z").getUTCDate()}</span>
              {d.count > 0 && <span className="text-[9px] font-bold leading-none mt-0.5">{d.count}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono">
        <span>{fmtDay(daily[0]?.date || "")}</span>
        <div className="flex items-center gap-1.5">
          <span>less</span>
          {[0.25, 0.5, 0.75, 1].map((o, i) => (
            <span key={i} className="w-3 h-3 rounded-sm border" style={{ background: hexWithAlpha(color, o), borderColor: hexWithAlpha(color, Math.min(1, o + 0.1)) }} />
          ))}
          <span>more</span>
        </div>
        <span>today</span>
      </div>

      {selected && (
        <div className="mt-5 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-foreground">{fmtFull(selected)}</p>
            <button onClick={() => setSelected(null)} className="text-[10px] text-muted-foreground hover:text-foreground font-mono uppercase">close</button>
          </div>
          {itemsForSelected.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">
              {(daily.find(d => d.date === selected)?.count || 0) > 0
                ? "Activity that day is older than the recent feed shows. Open the agent's full chat for the transcript."
                : "Nothing happened on this day."}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {itemsForSelected.map(r => (
                <li key={r.id} className="py-2">
                  <p className="text-xs font-medium text-foreground">{r.title}</p>
                  {r.body && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{r.body}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
